// docs/69 ST3 — the status badges: each status's 7×7 glyph (core/sim/statuses.ts) inked on its
// colour, in a 1 px dark rim, drawn once into a 9×9 canvas; and the few shapes the auras need
// that the spells' textures don't have (a web, a halo, a pool).
import Phaser from 'phaser';
import { STATUS_INFO, cssColour, glyphInk } from '../../core/sim/statuses';
import type { StatusKind } from '../../core/sim/types';

export const BADGE_PX = 9;
export const badgeKey = (kind: StatusKind): string => `fk-st-${kind}`;
export const ST_TEX = { web: 'fk-st-web', halo: 'fk-st-halo', pip: 'fk-st-pip', pool: 'fk-st-pool', crest: 'fk-st-crest' } as const;

function canvas(tm: Phaser.Textures.TextureManager, key: string, w: number, h: number, draw: (x: CanvasRenderingContext2D) => void, linear = false): void {
  if (tm.exists(key)) return;
  const c = tm.createCanvas(key, w, h)!;
  const x = c.getContext();
  x.clearRect(0, 0, w, h);
  draw(x);
  c.refresh();
  if (linear) c.setFilter(Phaser.Textures.FilterMode.LINEAR);
}

/** A rounded 9×9 badge: a dark rim, the status's colour, the glyph inked over it. */
function badge(x: CanvasRenderingContext2D, colour: number, icon: readonly string[]): void {
  x.fillStyle = '#0b1020'; x.fillRect(1, 0, 7, 9); x.fillRect(0, 1, 9, 7);
  x.fillStyle = cssColour(colour); x.fillRect(1, 1, 7, 7);
  icon.forEach((row, j) => [...row].forEach((ch, i) => { if (ch === '#') { x.fillStyle = glyphInk(colour); x.fillRect(i + 1, j + 1, 1, 1); } }));
}

export function ensureStatusTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  for (const [kind, info] of Object.entries(STATUS_INFO)) canvas(tm, badgeKey(kind as StatusKind), BADGE_PX, BADGE_PX, (x) => badge(x, info.colour, info.icon));
  canvas(tm, ST_TEX.pip, 2, 2, (x) => { x.fillStyle = '#fff'; x.fillRect(0, 0, 2, 2); });
  // a spider's web seen flat: eight spokes and three sagging rings
  canvas(tm, ST_TEX.web, 48, 48, (x) => {
    x.strokeStyle = 'rgba(255,255,255,0.95)'; x.lineWidth = 1;
    const spoke = (i: number) => (i / 8) * Math.PI * 2 + 0.2;
    for (let i = 0; i < 8; i++) { x.beginPath(); x.moveTo(24, 24); x.lineTo(24 + Math.cos(spoke(i)) * 23, 24 + Math.sin(spoke(i)) * 23); x.stroke(); }
    for (const r of [7, 13, 19]) {
      x.beginPath();
      for (let i = 0; i <= 8; i++) {
        const a0 = spoke(i % 8), a1 = spoke((i + 1) % 8), mid = (a0 + (i === 7 ? a1 + Math.PI * 2 : a1)) / 2;
        if (i === 0) x.moveTo(24 + Math.cos(a0) * r, 24 + Math.sin(a0) * r);
        if (i < 8) x.quadraticCurveTo(24 + Math.cos(mid) * r * 0.82, 24 + Math.sin(mid) * r * 0.82, 24 + Math.cos(a1) * r, 24 + Math.sin(a1) * r);
      }
      x.stroke();
    }
  }, true);
  // a halo: a bright thin ring with a soft glow either side
  canvas(tm, ST_TEX.halo, 32, 32, (x) => {
    x.strokeStyle = 'rgba(255,255,255,0.35)'; x.lineWidth = 6; x.beginPath(); x.arc(16, 16, 11, 0, Math.PI * 2); x.stroke();
    x.strokeStyle = '#fff'; x.lineWidth = 2; x.beginPath(); x.arc(16, 16, 11, 0, Math.PI * 2); x.stroke();
  }, true);
  // docs/104 PD2: a paladin's shield — a kite shield, filled pale, with a sun cross on it
  canvas(tm, ST_TEX.crest, 12, 14, (x) => {
    const rows = [
      '.##########.', '#++++++++++#', '#++++##++++#', '#++++##++++#', '#++######++#', '#++######++#', '#++++##++++#',
      '#++++##++++#', '.#+++##+++#.', '.#+++##+++#.', '..#++##++#..', '...#+##+#...', '....#++#....', '.....##.....'];
    rows.forEach((row, j) => [...row].forEach((ch, i) => {
      const a = ch === '#' ? 1 : ch === '+' ? 0.45 : 0;
      if (a) { x.fillStyle = `rgba(255,255,255,${a})`; x.fillRect(i, j, 1, 1); }
    }));
  });
  // a pool: an uneven blot
  canvas(tm, ST_TEX.pool, 32, 16, (x) => {
    x.fillStyle = '#fff';
    for (const [cx, cy, rx, ry] of [[16, 8, 11, 5], [9, 9, 5, 3.5], [24, 7, 5, 3], [18, 11, 6, 3]] as const) { x.beginPath(); x.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); x.fill(); }
  }, true);
}
